import { useState } from 'react';
import { motion } from 'framer-motion';
import { Video, Wand2, Settings, Loader2, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { useGenerateVideo, useProviders, useUserQuota } from '../hooks/useVideo';
import type { VideoPrompt, VideoProvider } from '../types';

const aspectRatios = [
  { value: '16:9', label: '16:9', desc: 'Landscape' },
  { value: '9:16', label: '9:16', desc: 'Portrait' },
  { value: '1:1', label: '1:1', desc: 'Square' },
  { value: '4:3', label: '4:3', desc: 'Classic' },
] as const;

const resolutions = [
  { value: '720p', label: '720p', desc: 'HD' },
  { value: '1080p', label: '1080p', desc: 'Full HD' },
  { value: '4k', label: '4K', desc: 'Ultra HD' },
] as const;

const fpsOptions = [
  { value: 24, label: '24 fps', desc: 'Cinematic' },
  { value: 30, label: '30 fps', desc: 'Standard' },
  { value: 60, label: '60 fps', desc: 'Smooth' },
] as const;

export default function GeneratePage() {
  const navigate = useNavigate();
  const { data: providers } = useProviders();
  const { data: quota } = useUserQuota();
  const generateMutation = useGenerateVideo();

  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState<VideoPrompt['aspectRatio']>('16:9');
  const [resolution, setResolution] = useState<VideoPrompt['resolution']>('1080p');
  const [fps, setFps] = useState<VideoPrompt['fps']>(30);
  const [provider, setProvider] = useState<VideoProvider | 'auto'>('auto');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    if (quota && quota.credits.remaining < 1) {
      toast.error('Insufficient credits');
      return;
    }

    try {
      const videoPrompt: VideoPrompt = {
        text: prompt,
        negativePrompt: negativePrompt || undefined,
        duration,
        aspectRatio,
        resolution,
        fps,
      };

      const job = await generateMutation.mutateAsync({
        prompt: videoPrompt,
        provider: provider === 'auto' ? undefined : provider,
      });

      toast.success('Video generation started!');
      navigate(`/jobs/${job.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start generation');
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-4">Generate Video</h1>
          <p className="text-gray-400">
            Describe your video and let AI bring it to life
          </p>
          {quota && (
            <div className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-full">
              <span className="text-gray-400">Credits:</span>
              <span className="text-primary-400 font-semibold">{quota.credits.remaining}</span>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          {/* Prompt Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Wand2 className="w-4 h-4 inline mr-2" />
              Describe your video
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A majestic eagle soaring through golden clouds at sunset, cinematic slow motion, dramatic lighting..."
              className="input-field min-h-[120px] resize-none"
              maxLength={2000}
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-500">{prompt.length}/2000</span>
            </div>
          </div>

          {/* Basic Settings */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Duration: {duration}s
              </label>
              <input
                type="range"
                min={1}
                max={60}
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
              />
            </div>

            {/* Aspect Ratio */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Aspect Ratio
              </label>
              <div className="grid grid-cols-4 gap-2">
                {aspectRatios.map((ar) => (
                  <button
                    key={ar.value}
                    onClick={() => setAspectRatio(ar.value)}
                    className={clsx(
                      'py-2 rounded-lg text-sm transition-all',
                      aspectRatio === ar.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    )}
                  >
                    {ar.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                AI Model
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as VideoProvider | 'auto')}
                className="input-field"
              >
                <option value="auto">Auto (Recommended)</option>
                {providers?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <Settings className="w-4 h-4" />
            <span>Advanced Settings</span>
            <ChevronDown className={clsx('w-4 h-4 transition-transform', showAdvanced && 'rotate-180')} />
          </button>

          {/* Advanced Settings */}
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-6 mb-6 pt-4 border-t border-gray-800"
            >
              {/* Negative Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Negative Prompt (Optional)
                </label>
                <textarea
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  placeholder="blur, low quality, distorted, watermark..."
                  className="input-field min-h-[80px] resize-none"
                  maxLength={500}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Resolution */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Resolution
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {resolutions.map((res) => (
                      <button
                        key={res.value}
                        onClick={() => setResolution(res.value)}
                        className={clsx(
                          'py-2 rounded-lg text-sm transition-all',
                          resolution === res.value
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        )}
                      >
                        {res.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* FPS */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Frame Rate
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {fpsOptions.map((f) => (
                      <button
                        key={f.value}
                        onClick={() => setFps(f.value)}
                        className={clsx(
                          'py-2 rounded-lg text-sm transition-all',
                          fps === f.value
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        )}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={generateMutation.isPending || !prompt.trim()}
            className="btn-primary w-full flex items-center justify-center space-x-2"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Starting...</span>
              </>
            ) : (
              <>
                <Video className="w-5 h-5" />
                <span>Generate Video</span>
              </>
            )}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
